import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { CloseOutlined } from '@ant-design/icons';
import { Modal, Skeleton, message } from 'antd';
import { connect } from 'react-redux';
import QRCode from 'qrcode';

import { checkPayResult, createOrder, getUserBenefit } from '@/api';
import store from '@/store';
import { AIZhuanhuacelueExposure } from '@/utils/sensorsFunc';
import wxLogo from '@/assets/images/newVip/wx.png';
import zfbLogo from '@/assets/images/newVip/zfb.png';

import s from './index.module.less';

const PAY_TYPES = [
  { value: 0, label: '微信支付', icon: wxLogo },
  { value: 1, label: '支付宝', icon: zfbLogo },
];

const getCZSource = () => {
  const aiSource = Number(store.getState().app.ai_source);
  if (aiSource === 300) return '3';
  return [6, 7, 10, 11, 12, 30].includes(aiSource) ? '2' : '1';
};

const RetentionRechargeModal = ({
  open,
  packageInfo,
  onClose,
  userInfo,
  setUserInfo,
  setUserTemporaryTops,
  setRechargeSuccessVersion,
}) => {
  const [payType, setPayType] = useState(0);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [orderNo, setOrderNo] = useState('');
  const [loading, setLoading] = useState(false);
  const [expired, setExpired] = useState(false);
  const [frameLoaded, setFrameLoaded] = useState(false);
  const timerRef = useRef(null);
  const requestIdRef = useRef(0);
  const currentOrderRef = useRef('');
  const checkingOrderRef = useRef('');
  const openRef = useRef(open);
  const exposedRef = useRef(false);

  useEffect(() => {
    openRef.current = open;

    if (open && !exposedRef.current) {
      AIZhuanhuacelueExposure(3);
      exposedRef.current = true;
    } else if (!open) {
      exposedRef.current = false;
    }
  }, [open]);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const createPayment = useCallback(async (nextPayType) => {
    if (!packageInfo?.id) return;

    const requestId = ++requestIdRef.current;
    clearTimer();
    setLoading(true);
    setExpired(false);
    setFrameLoaded(false);
    setQrCodeUrl('');
    setOrderNo('');
    currentOrderRef.current = '';
    checkingOrderRef.current = '';

    const params = {
      package_id: packageInfo.id,
      cz_type: nextPayType,
      qrcode_size: '140',
      is_union: 0,
      coupon_id: 0,
      order_type: '51',
      entrance: '12',
      cz_source: getCZSource(),
      member_location: 35,
    };
    const formData = new FormData();
    formData.append('data', JSON.stringify(params));

    try {
      const res = await createOrder(formData);
      if (!openRef.current || requestId !== requestIdRef.current) return;
      if (res?.status === 0 || !res?.data?.jkma || !res?.data?.qrcode) {
        message.error(res?.msg || '请求出错了，请稍候重试');
        return;
      }

      const { jkma, qrcode } = res.data;
      currentOrderRef.current = jkma;
      setOrderNo(jkma);
      if (nextPayType === 1) {
        setQrCodeUrl(qrcode);
      } else {
        try {
          const dataUrl = await QRCode.toDataURL(String(qrcode), { width: 140, margin: 1 });
          if (openRef.current && requestId === requestIdRef.current) setQrCodeUrl(dataUrl);
        } catch {
          if (openRef.current && requestId === requestIdRef.current) {
            message.error('二维码加载失败，请稍后重试');
          }
        }
      }
    } catch {
      if (openRef.current && requestId === requestIdRef.current) {
        message.error('二维码加载失败，请稍后重试');
      }
    } finally {
      if (openRef.current && requestId === requestIdRef.current) setLoading(false);
    }
  }, [clearTimer, packageInfo]);

  useEffect(() => {
    if (!open || !packageInfo?.id) return;
    setPayType(0);
    void createPayment(0);
  }, [open, packageInfo?.id, createPayment]);

  useEffect(() => {
    if (!open || !qrCodeUrl || !orderNo) return;

    const pollingOrderNo = orderNo;
    timerRef.current = setInterval(async () => {
      if (checkingOrderRef.current === pollingOrderNo) return;
      checkingOrderRef.current = pollingOrderNo;

      const formData = new FormData();
      formData.append('service', 'll.order.check');
      formData.append('jkma', pollingOrderNo);
      formData.append('cz_channel', '51');
      formData.append('is_union', '0');

      try {
        const res = await checkPayResult(formData);
        if (!openRef.current || currentOrderRef.current !== pollingOrderNo) return;
        if (res?.status === 200) {
          clearTimer();
          const benefitRes = await getUserBenefit();
          if (!openRef.current || currentOrderRef.current !== pollingOrderNo) return;
          if (benefitRes?.status === 200 && benefitRes?.data) {
            const updatedUserInfo = { ...userInfo, ...benefitRes.data };
            setUserInfo(updatedUserInfo);
            setUserTemporaryTops(benefitRes.data.total_tops);
            localStorage.setItem('userInfo', JSON.stringify(updatedUserInfo));
            setRechargeSuccessVersion(store.getState().edit.rechargeSuccessVersion + 1);
          }
          message.success('充值成功');
          onClose();
        } else if (res?.status === 401) {
          clearTimer();
          setExpired(true);
        }
      } catch {
        // 单次查单失败时保留轮询，避免网络抖动中断支付确认。
      } finally {
        if (checkingOrderRef.current === pollingOrderNo) checkingOrderRef.current = '';
      }
    }, 2000);

    return clearTimer;
  }, [clearTimer, onClose, open, orderNo, qrCodeUrl, setRechargeSuccessVersion, setUserInfo, setUserTemporaryTops, userInfo]);

  useEffect(() => () => {
    requestIdRef.current += 1;
    currentOrderRef.current = '';
    checkingOrderRef.current = '';
    clearTimer();
  }, [clearTimer]);

  const handleClose = () => {
    requestIdRef.current += 1;
    currentOrderRef.current = '';
    checkingOrderRef.current = '';
    clearTimer();
    setQrCodeUrl('');
    setOrderNo('');
    onClose();
  };

  const handlePayTypeChange = (nextPayType) => {
    if (nextPayType === payType) return;
    setPayType(nextPayType);
    void createPayment(nextPayType);
  };

  const displayedPrice = `¥${packageInfo?.package_fee || '--'}`;

  return (
    <Modal
      wrapClassName={s.modal}
      width={424}
      zIndex={1500}
      open={open}
      onCancel={handleClose}
      centered
      destroyOnClose
      closable={false}
      footer={null}
    >
      <div className={s.panel}>
        <button className={s.closeButton} type="button" aria-label="关闭" onClick={handleClose}>
          <CloseOutlined />
        </button>

        <div className={s.payTypes}>
          {PAY_TYPES.map((item) => (
            <button
              key={item.value}
              type="button"
              className={payType === item.value ? s.activePayType : ''}
              onClick={() => handlePayTypeChange(item.value)}
            >
              <img src={item.icon} alt="" />
              <span>{item.label}</span>
            </button>
          ))}
        </div>

        <div className={s.paymentArea}>
          <div className={s.qrCode}>
            {!loading && qrCodeUrl && payType === 1 && (
              <iframe
                title="支付宝支付"
                src={qrCodeUrl}
                width="140"
                height="140"
                frameBorder="0"
                scrolling="no"
                style={{ visibility: frameLoaded ? 'visible' : 'hidden' }}
                onLoad={() => setFrameLoaded(true)}
              />
            )}
            {!loading && qrCodeUrl && payType !== 1 && <img src={qrCodeUrl} alt="支付二维码" />}
            {!expired && (loading || !qrCodeUrl || (payType === 1 && !frameLoaded)) && (
              <Skeleton.Image active style={{ width: 116, height: 116 }} />
            )}
            {expired && (
              <button type="button" className={s.expired} onClick={() => createPayment(payType)}>
                <span>二维码失效</span>
                <span>请点击<em>刷新</em></span>
              </button>
            )}
          </div>
          <div className={s.payPrice}>{displayedPrice}</div>
        </div>
      </div>
    </Modal>
  );
};

const mapStateToProps = ({ app }) => ({
  userInfo: app.userInfo,
});

const mapDispatchToProps = (dispatch) => ({
  setUserInfo: (payload) => dispatch({ type: 'app/setUserInfo', payload }),
  setUserTemporaryTops: (payload) => dispatch({ type: 'edit/setUserTemporaryTops', payload }),
  setRechargeSuccessVersion: (payload) => dispatch({ type: 'edit/setRechargeSuccessVersion', payload }),
});

export default memo(connect(mapStateToProps, mapDispatchToProps)(RetentionRechargeModal));
